/**
 * Trip Types - Single Source of Truth for Trip-Related Types
 *
 * DRY Compliance: Consolidates trip type definitions for:
 * - backend-tms/src/trips/ (future Trip module)
 * - backend-tms/src/ai-routing/ (Trip-aware AI optimization)
 * - apps/web-tms/src/app/[locale]/trips/ (Trip UI)
 *
 * Usage:
 * - Backend: Import interfaces, create DTO classes with decorators extending these
 * - Frontend: Import directly from @axon/common
 *
 * Integration: This module is part of the Trip + AI Route Optimization combined implementation.
 * See docs/planning/__archived__/trip-entity-migration.md for full details.
 */

import { RouteMetrics, Coordinate } from './route.types';

// Re-export for convenience
export type { RouteMetrics, Coordinate };

// ==================== Trip Status ====================

/**
 * Trip lifecycle status
 */
export enum TripStatus {
  /** Trip is being planned, not yet started */
  PLANNED = 'planned',
  /** Trip is awaiting validation before execution */
  VALIDATION = 'validation',
  /** Trip has started, vehicle is in transit */
  IN_PROGRESS = 'in_progress',
  /** Trip completed successfully */
  COMPLETED = 'completed',
  /** Trip was cancelled before completion */
  CANCELLED = 'cancelled',
  /** Trip is temporarily suspended */
  SUSPENDED = 'suspended',
}

// ==================== Trip Metrics ====================

/**
 * Trip-level metrics extending RouteMetrics for multi-route aggregation
 * Used for trip cost rollup and ML pattern learning
 */
export interface TripMetrics extends RouteMetrics {
  /** Number of routes in the trip */
  totalRoutes: number;
  /** Number of completed routes */
  completedRoutes: number;
  /** Number of drivers involved (for multi-driver trips) */
  driverCount: number;
  /** Total HOS (Hours of Service) violations across all routes */
  hosViolations: number;
  /** Aggregated cost from all routes */
  aggregatedCost: number;
  /** Trip-level on-time score (0-100) */
  tripOnTimeScore: number;
}

/**
 * Trip performance data for ML pattern learning
 * Stored in trip_performance table for historical analysis
 */
export interface TripPerformance {
  tripId: string;
  tenantId: string;
  executionDate: Date;
  /** Planned total distance in km */
  plannedDistance: number;
  /** Actual total distance in km */
  actualDistance: number;
  /** Number of routes in this trip */
  totalRoutes: number;
  /** HOS violations detected */
  hosViolations: number;
  /** Weather impact score (0-100, 100 = no impact) */
  weatherImpactScore: number;
  /** Traffic impact score (0-100, 100 = no impact) */
  trafficImpactScore: number;
}

// ==================== Trip Core Types ====================

/**
 * Trip metadata for extended trip information
 */
export interface TripMetadata {
  /** ELD (Electronic Logging Device) data for US compliance */
  eldData?: Record<string, unknown>;
  /** Driver notes and comments */
  driverNotes?: string;
  /** Customs tracking for cross-border trips */
  customsInfo?: {
    borderCrossings?: string[];
    declarationNumbers?: string[];
  };
  /** Additional metadata */
  [key: string]: unknown;
}

/**
 * Trip entity type for API responses and frontend
 */
export interface Trip {
  id: string;
  tenantId: string;
  /** Unique trip number (e.g., TRP-20260120-ABC123) */
  tripNumber: string;
  /** Assigned vehicle ID */
  vehicleId?: string;
  /** Primary driver ID */
  primaryDriverId?: string;
  /** Trip status */
  status: TripStatus;
  /** Trip start time */
  startTime?: Date;
  /** Trip end time */
  endTime?: Date;
  /** Total distance in km */
  totalDistanceKm?: number;
  /** Total cost */
  totalCost?: number;
  /** Trip metadata */
  metadata?: TripMetadata;
  /** Creation timestamp */
  createdAt: Date;
  /** Last update timestamp */
  updatedAt: Date;
}

/**
 * Trip-Route junction representing route sequence within a trip
 */
export interface TripRoute {
  id: string;
  tripId: string;
  routeId: string;
  /** Sequence number of this route in the trip (1, 2, 3...) */
  sequenceNumber: number;
  /** Creation timestamp */
  createdAt: Date;
}

/**
 * Trip route with full route details for trip detail response
 * Extends TripRoute with actual route data
 */
export interface TripRouteDetail extends TripRoute {
  /** Actual route ID for navigation */
  routeId: string;
  /** Route number (e.g., RT-001) */
  routeNumber: string;
  /** Route name */
  name: string;
  /** Route status */
  status: string;
  /** Origin location */
  origin: string;
  /** Destination location */
  destination: string;
  /** Distance in kilometers */
  distanceKm: number;
  /** Duration in minutes */
  durationMinutes: number;
  /** Number of stops/waypoints */
  stopsCount: number;
}

// ==================== Trip Request/Response ====================

/**
 * Request to create a new trip
 */
export interface CreateTripRequest {
  /** Optional custom trip number (auto-generated if not provided) */
  tripNumber?: string;
  /** Optional human-readable trip name (B-132: surfaced from the create form) */
  name?: string;
  /** Optional planned date for the trip — `YYYY-MM-DD` (B-132). */
  plannedDate?: string;
  /** Vehicle to assign */
  vehicleId?: string;
  /** Primary driver */
  primaryDriverId?: string;
  /** Route IDs to include in trip (in sequence order) */
  routeIds?: string[];
  /** Initial metadata */
  metadata?: TripMetadata;
}

/**
 * Request to update an existing trip
 */
export interface UpdateTripRequest {
  /** Update vehicle assignment */
  vehicleId?: string;
  /** Update primary driver */
  primaryDriverId?: string;
  /** Update status */
  status?: TripStatus;
  /** Update start time */
  startTime?: Date;
  /** Update end time */
  endTime?: Date;
  /** Update metadata */
  metadata?: TripMetadata;
}

/**
 * Request to add routes to a trip
 */
export interface AddRoutesToTripRequest {
  /** Route IDs to add */
  routeIds: string[];
  /** Insert at specific position (default: end of trip) */
  insertAtPosition?: number;
}

/**
 * Trip response with related data
 */
export interface TripResponse extends Trip {
  /** Routes in this trip (ordered by sequence) with full route details */
  routes?: TripRouteDetail[];
  /** Calculated metrics */
  metrics?: TripMetrics;
  /** Vehicle details (if loaded) */
  vehicle?: {
    id: string;
    plateNumber: string;
    type: string;
  };
  /** Driver details (if loaded) */
  primaryDriver?: {
    id: string;
    name: string;
    licenseNumber: string;
  };
}

// ==================== Trip Query ====================

/**
 * Query parameters for trip list
 */
export interface TripQueryParams {
  /** Filter by status */
  status?: TripStatus | TripStatus[];
  /** Filter by vehicle */
  vehicleId?: string;
  /** Filter by driver */
  driverId?: string;
  /** Filter by date range (start) */
  startDateFrom?: Date;
  /** Filter by date range (end) */
  startDateTo?: Date;
  /** Search by trip number */
  search?: string;
  /** Page number */
  page?: number;
  /** Items per page */
  limit?: number;
  /** Sort field */
  sortBy?: 'tripNumber' | 'startTime' | 'endTime' | 'totalCost' | 'createdAt';
  /** Sort order */
  sortOrder?: 'ASC' | 'DESC';
}

// ==================== Trip Bounding Box ====================

/**
 * Bounding box for trip corridor (used for weather/traffic API queries)
 */
export interface TripBoundingBox {
  /** Northwest corner */
  northwest: Coordinate;
  /** Southeast corner */
  southeast: Coordinate;
  /** Center point */
  center: Coordinate;
  /** Maximum distance from center in km */
  maxRadius: number;
}
